/**
 * GENERATED by /docs-writer from docs/transforge-operations.md — DO NOT EDIT BY HAND.
 * Edit the markdown and run `/docs-writer update docs/transforge-operations.md`.
 *
 * source-sha: b1cb1c213a68cbdf63007f731b6e9ba98bf68c64
 * sources (from the markdown's "Sources" section):
 *   - Live production scan, 2026-10-12: kubectl and doctl (read-only) on both DigitalOcean clusters and the Oracle Cloud cluster, certificate objects, HTTPS probes
 *   - os-transforge GitLab project: pipeline 2917158204 and its deploy jobs (finished 2026-10-08), merge requests 203 to 212, branch heads on 2026-10-12
 *   - KEDA operator logs and lease on the DigitalOcean worker cluster, read 2026-10-12
 *   - Production MongoDB, collection assemblies: failure causes for 2026-10-05 to 2026-10-11, read 2026-10-12
 *   - os-transforge at origin/main b64cf43d: .gitlab-ci.yml, maintenance-service.ts (cleanup, archive, purge), app.ts (shutdown), the production manifests for both clouds
 *   - TransForge hourly stats rollup, 2026-10-05 to 2026-10-11 (assembly_stats, robot_stats)
 *   - infras-app docs/scheduler.md (job transforge-stats)
 *   - conversation, 2026-10-01 to 2026-10-12 (status read after cleanup, console read path)
 */
import { Link } from 'react-router';
import { DocsToc } from '../components/DocsToc';

const TOC = [
  { id: 'how-a-release-reaches-production', label: 'How a release reaches production' },
  { id: 'how-it-scales', label: 'How it scales' },
  { id: 'how-long-files-and-records-live', label: 'How long files and records live' },
  { id: 'where-to-look', label: 'Where to look' },
  { id: 'what-needs-attention-on-12-october-2026', label: 'What needs attention on 12 October 2026' },
];

export function TransforgeOperationsDocsPage() {
  return (
    <>
      <div className="page doc" id="top">
        <p className="doc-eyebrow">Media · Runbook</p>
        <h1 className="doc-title">Deploy and operate TransForge</h1>
        <p className="doc-lede">
          Production <Link to="/docs/transforge">TransForge</Link> runs release <code>b64cf43d</code> on both
          clouds, deployed on 8 October 2026. A merge to <code>main</code> releases nothing: a person starts one
          deploy job for each service on each cloud. This page covers the release path, the scaling limits, how
          long data lives, and the issues open on 12 October 2026.
        </p>
        <p className="doc-meta">
          Updated <time dateTime="2026-10-12">12 Oct 2026</time>
        </p>

        <h2 className="doc-h2" id="how-a-release-reaches-production">How a release reaches production</h2>
        <ol className="doc-list">
          <li>
            A change merges into the <code>sandbox</code> branch, and <code>sandbox</code> merges into{' '}
            <code>main</code>.
          </li>
          <li>
            The pipeline on <code>main</code> runs the tests and builds each image twice: for x86, which
            DigitalOcean uses, and for ARM, which Oracle Cloud uses.
          </li>
          <li>The pipeline updates the settings and secrets on both clouds. This step runs without a person.</li>
          <li>
            A person starts the deploy jobs: seven for DigitalOcean and six for Oracle Cloud, one for each
            service. Until then production keeps the previous release.
          </li>
          <li>Each job sets the new image and waits up to 10 minutes for the pods to roll.</li>
          <li>
            A person starts <code>verify:prod-oci</code>, which checks that the Oracle Cloud API answers its
            health check. The console has a deploy job of its own.
          </li>
        </ol>
        <p className="doc-p">
          A release has no tag. Its name is the short commit ID, and the pods carry it as their image tag. On 12
          October 2026 <code>main</code> has no commit that is not released.
        </p>
        <aside className="doc-note">
          <span className="doc-note-label">Note</span>
          <p>
            During a roll, an API pod stops taking new requests and waits up to 30 seconds for the requests and
            uploads it is serving. An upload that is still running after 30 seconds is cut off.
          </p>
        </aside>

        <h2 className="doc-h2" id="how-it-scales">How it scales</h2>
        <p className="doc-p">
          Only the worker groups on DigitalOcean scale by themselves. Everything else has a fixed size.
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Workload</th>
                <th>On DigitalOcean</th>
                <th>Scales on</th>
                <th>On Oracle Cloud</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>robot-workers</code></td>
                <td>3 to 20 pods</td>
                <td>Processor use above 70%</td>
                <td>3 pods, fixed</td>
              </tr>
              <tr>
                <td><code>robot-workers-document-thumbs</code></td>
                <td>4 to 35 pods</td>
                <td>Jobs waiting in its queue</td>
                <td>2 pods, fixed</td>
              </tr>
              <tr>
                <td><code>robot-workers-playwright</code></td>
                <td>1 to 5 pods</td>
                <td>Jobs waiting in its queue</td>
                <td>1 pod, fixed</td>
              </tr>
              <tr>
                <td><code>robot-workers-video-encode</code></td>
                <td>1 to 4 pods</td>
                <td>Jobs waiting in its queue</td>
                <td>1 pod, fixed</td>
              </tr>
              <tr>
                <td><code>api-gateway</code></td>
                <td>8 pods, fixed</td>
                <td>Nothing</td>
                <td>2 pods, fixed</td>
              </tr>
              <tr>
                <td><code>assembly-service</code></td>
                <td>4 pods, fixed</td>
                <td>Nothing</td>
                <td>2 pods, fixed</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          The number of nodes is fixed on both clouds: 16 on DigitalOcean and 5 on Oracle Cloud. Pods can scale up
          only as far as those nodes have room.
        </p>
        <p className="doc-p">
          Load is low. On 12 October 2026 no node used more than 14% of its processors. Memory is the tighter
          resource: a <code>robot-workers</code> pod asks for 1.5 GiB and was using up to 5.3 GiB.
        </p>

        <h2 className="doc-h2" id="how-long-files-and-records-live">How long files and records live</h2>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>What</th>
                <th>Lives for</th>
                <th>Then</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Working files and results</strong> on the file share</td>
                <td>1.5 days after the assembly ends on DigitalOcean, and 2 days on Oracle Cloud</td>
                <td>The assembly&rsquo;s whole folder is deleted.</td>
              </tr>
              <tr>
                <td><strong>Result archive</strong></td>
                <td>Starts 60 minutes after the assembly ends</td>
                <td>A copy of the result is written to object storage, and the record keeps its address.</td>
              </tr>
              <tr>
                <td><strong>Source files of a failed portal upload</strong></td>
                <td>Archived with the result</td>
                <td>A copy of each source file is written to the same object storage.</td>
              </tr>
              <tr>
                <td><strong>Assembly record</strong> in the database</td>
                <td>45 days after the assembly ends</td>
                <td>The record is deleted, if its result is archived and its files are cleaned.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          The assembly service runs all three jobs. Cleanup and archive run every 30 minutes on each cloud for
          that cloud&rsquo;s own assemblies. The purge of old records runs on DigitalOcean.
        </p>
        <aside className="doc-note doc-note--warn">
          <span className="doc-note-label">Warning</span>
          <p>
            After cleanup, a status read answers with the right status and <strong>no results</strong>. The files
            that held the results are gone. Read the archived copy instead.
          </p>
        </aside>

        <h2 className="doc-h2" id="where-to-look">Where to look</h2>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Question</th>
                <th>Where</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Is the API up?</td>
                <td>
                  <code>/healthz</code> on each of the three hosts.{' '}
                  <Link to="/docs/transforge-multi-cloud">Multi-cloud</Link> lists them.
                </td>
              </tr>
              <tr>
                <td>What happened to one assembly?</td>
                <td>
                  The TransForge console at <code>transforge.optisigns.com</code>. It reads through DigitalOcean,
                  so an assembly that Oracle Cloud created shows no results there.
                </td>
              </tr>
              <tr>
                <td>How much ran, and how much failed?</td>
                <td>
                  The hourly statistics that the infras scheduler job <code>transforge-stats</code> collects at 5
                  minutes past each hour. No dashboard page shows them yet.
                </td>
              </tr>
              <tr>
                <td>Are jobs waiting?</td>
                <td>
                  The queue gauges that each worker group reports. All 17 queues on DigitalOcean had nothing
                  waiting on 12 October 2026.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">Certificates renew by themselves about 30 days before they expire.</p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Host</th>
                <th>Certificate expires</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Console API</td>
                <td>3 December 2026</td>
              </tr>
              <tr>
                <td><code>transforge-api-oci.optisigns.com</code></td>
                <td>22 December 2026</td>
              </tr>
              <tr>
                <td><code>transforge-api-global.optisigns.com</code></td>
                <td>30 December 2026</td>
              </tr>
              <tr>
                <td><code>transforge-api.optisigns.com</code></td>
                <td>9 January 2027</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 className="doc-h2" id="what-needs-attention-on-12-october-2026">What needs attention on 12 October 2026</h2>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Issue</th>
                <th>Effect</th>
                <th>What fixes it</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>The video worker&rsquo;s roll setting on Oracle Cloud exists on the cluster only.</strong>{' '}
                  It was set by hand on 12 October 2026.
                </td>
                <td>
                  Applying the manifest from the repository puts the old setting back, and the next roll of that
                  worker then never finishes.
                </td>
                <td>Merge the manifest change that carries the setting.</td>
              </tr>
              <tr>
                <td>
                  <strong>The autoscaler on DigitalOcean restarts.</strong> The KEDA operator exits when the
                  cluster&rsquo;s API server drops its connections for about 10 seconds and it loses its leader
                  lock. It restarted four times from 8 to 12 October 2026.
                </td>
                <td>
                  Autoscaling of three worker groups pauses for 45 to 60 seconds after each restart. Pod counts
                  stay as they were.
                </td>
                <td>
                  Run two operator pods, so that a standby takes over. What starts the dropped connections is not
                  established.
                </td>
              </tr>
              <tr>
                <td>
                  <strong>The screenshot job fails often.</strong> 4,975 of its 50,357 assemblies failed in seven
                  days.
                </td>
                <td>It is the largest source of failed assemblies.</td>
                <td>
                  Most failures come from the address the customer gave: its host name does not resolve (2,011),
                  the page takes more than 60 seconds to load (1,497), or it is not a URL (573). Check the address
                  before the job creates an assembly.
                </td>
              </tr>
              <tr>
                <td><strong>A status read can look finished and empty.</strong> It happens on the wrong cloud, and on any cloud after cleanup.</td>
                <td>A client that trusts the answer treats the assembly as done with no output.</td>
                <td>Three merge requests are open: a runbook correction, the research, and the assembly ID convention.</td>
              </tr>
              <tr>
                <td><strong>Companion ran out of memory.</strong> It restarted 9 times, last on 2 October 2026.</td>
                <td>It has one pod, so imports from a customer&rsquo;s cloud drive stop while it restarts.</td>
                <td>It has a 1 GiB memory limit. It has not restarted since.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          Both video deploy jobs of 8 October 2026 reported a failure, because neither roll finished inside the
          10-minute wait. The roll on DigitalOcean finished afterwards. The roll on Oracle Cloud finished on
          12 October 2026, once its roll setting stopped the old pod first.
        </p>

        <p className="doc-back">
          <a href="#top">Back to top ↑</a>
        </p>
      </div>
      <DocsToc items={TOC} />
    </>
  );
}
